import React, { createContext, use, useCallback, useMemo, useState } from 'react';
import { AnimatePresence, m } from '@/lib/motion';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { toastMotion, transitionDefault } from '@/lib/motion-variants';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { UI_TIMING } from '@/constants';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  toasts: Toast[];
  toast: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const useToast = () => {
  const context = use(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast variants
const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg',
  {
    variants: {
      variant: {
        success: 'border-green-200 bg-green-50 text-green-900',
        error: 'border-red-200 bg-red-50 text-red-900',
        warning: 'border-amber-200 bg-amber-50 text-amber-900',
        info: 'border-secondary-200 bg-secondary-50 text-foreground',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const iconVariants = {
  success: { icon: CheckCircle, className: 'text-green-600' },
  error: { icon: AlertCircle, className: 'text-red-600' },
  warning: { icon: AlertTriangle, className: 'text-amber-600' },
  info: { icon: Info, className: 'text-secondary-600' },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (toastData: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = {
        id,
        duration: UI_TIMING.TOAST_DURATION,
        variant: 'info',
        ...toastData,
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto dismiss
      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => {
          dismiss(id);
        }, newToast.duration);
      }
    },
    [dismiss],
  );

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(() => ({ toasts, toast, dismiss, dismissAll }), [toasts, toast, dismiss, dismissAll]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div className='fixed top-0 right-0 z-[9999] flex max-h-screen w-full flex-col-reverse p-4 sm:top-auto sm:bottom-0 sm:right-0 sm:flex-col md:max-w-[420px]'>
      <AnimatePresence mode='popLayout'>
        {toasts.map((toast) => (
          <ToastComponent key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}

interface ToastComponentProps extends VariantProps<typeof toastVariants> {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastComponent({ toast, onDismiss }: ToastComponentProps) {
  const { icon: Icon, className: iconClassName } = iconVariants[toast.variant || 'info'];
  const isMobile = useIsMobileViewport();
  const slideY = isMobile ? -16 : 16;

  return (
    <m.div
      layout
      initial={{ opacity: 0, y: slideY }}
      animate={toastMotion.animate}
      exit={{ opacity: 0, y: isMobile ? -8 : 8 }}
      transition={transitionDefault}
      className={cn(toastVariants({ variant: toast.variant }), 'mb-2 last:mb-0')}
    >
      <div className='flex items-center space-x-3'>
        <Icon className={cn('h-5 w-5 shrink-0', iconClassName)} />
        <div className='flex-1 min-w-0'>
          {toast.title && <div className='text-sm font-medium'>{toast.title}</div>}
          <div className={cn('text-sm', toast.title && 'mt-1')}>{toast.message}</div>
          {toast.action && (
            <div className='mt-2'>
              <button
                type='button'
                onClick={toast.action.onClick}
                className='text-sm font-medium underline hover:no-underline'
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>
      </div>
      <button
        type='button'
        onClick={() => onDismiss(toast.id)}
        className='absolute top-1/2 right-2 -translate-y-1/2 text-current/50 hover:text-current transition-colors'
        aria-label='Close notification'
      >
        <X className='h-4 w-4' />
      </button>
    </m.div>
  );
}

// Convenience hook with common toast methods
const useToastHelpers = () => {
  const { toast } = useToast();

  return {
    success: (message: string, title?: string) => toast({ message, title, variant: 'success' }),
    error: (message: string, title?: string) => toast({ message, title, variant: 'error' }),
    warning: (message: string, title?: string) => toast({ message, title, variant: 'warning' }),
    info: (message: string, title?: string) => toast({ message, title, variant: 'info' }),
  };
};

// Context hooks are typically used with components, so we'll suppress the warning
// oxlint-disable-next-line react/only-export-components
export { useToast, useToastHelpers };
